/**
 * vizRegistry — decides which premium visualization renders a given artifact,
 * with graceful fallback to the existing ACTION_REGISTRY cards / generic chart.
 *
 * Two routers:
 *   - resolveChartViz(chart)  → by `chart.variant`, the backend tags its chart
 *     payload (treasury_fan, income_expense, scenario_compare, …) and we map it
 *     to the matching viz. No match → caller uses the generic ChartArtifact.
 *   - resolveResultViz(result) → by `result.type`, for action results. Returns
 *     a descriptor with `mode`:
 *       'replace' — render this viz INSTEAD of the registered ResultCard
 *                   (DETECT_SUSPICIOUS_* → AnomalyMatrix).
 *       'augment' — render this viz IN ADDITION to the ResultCard
 *                   (BULK_REMINDERS → AgingBars from the tier breakdown).
 *
 * Kept a router, not a second rendering engine: new viz plug in by adding a
 * variant entry (once its backend emitter exists) or a result branch.
 */

import { lazyWithRetry } from '@/lib/lazyWithRetry';

/**
 * Los nueve se cargan BAJO DEMANDA, y no por elegancia: siete de ellos traen
 * recharts, y este registro lo alcanzaba una cadena estática desde el arranque
 * —`pages/index.jsx` → `Layout` → `AIPanel` → `InlineMiniChart` → aquí—, así que
 * `vendor-charts` entraba en el grafo del entry y Vite le ponía `modulepreload`:
 * 114 KB comprimidos que descargaba TODO usuario, abriera o no una conversación
 * con gráfico. Medido en prod el 11-09-2026: el arranque baja 1041 KB en doce
 * ficheros, todos precargados.
 *
 * Este registro es un router: sus dos funciones devuelven `{ Component, props }` y
 * el que pinta hace `<viz.Component …/>`. Diferirlos sólo exige que ese punto de
 * render quede bajo `<Suspense>` — está en `ArtifactCard` y en `InlineMiniChart`,
 * dos ficheros.
 *
 * `lazyWithRetry` y no `lazy` pelado: es el contrato del repo, recarga una vez si
 * el hash del chunk quedó obsoleto tras un despliegue.
 */
const AnomalyMatrix = lazyWithRetry(() => import('./AnomalyMatrix'));
const AgingBars = lazyWithRetry(() => import('./AgingBars'));
const TreasuryFanChart = lazyWithRetry(() => import('./TreasuryFanChart'));
const IncomeExpenseArea = lazyWithRetry(() => import('./IncomeExpenseArea'));
const ScenarioCompare = lazyWithRetry(() => import('./ScenarioCompare'));
const CategoryTreemap = lazyWithRetry(() => import('./CategoryTreemap'));
const BreakEvenChart = lazyWithRetry(() => import('./BreakEvenChart'));
const WaterfallChart = lazyWithRetry(() => import('./WaterfallChart'));
const GaugeChart = lazyWithRetry(() => import('./GaugeChart'));

const PATTERN_RESULT_TYPES = new Set([
  'DETECT_SUSPICIOUS_INVOICES',
  'DETECT_SUSPICIOUS_EXPENSES',
  'DETECT_SUSPICIOUS_ACCOUNTING',
  'DETECT_SUSPICIOUS_PATTERNS',
]);

// Collection cycle tiers (recordatorio_facturas_vencidas) → aging segments.
// preventive 1-7d / standard 8-15d / formal 16-30d / urgent 31-45d / escalation >45d.
const TIER_SEGMENTS = [
  { key: 'preventive', label: 'Preventivo', tone: 'ok' },
  { key: 'standard', label: 'Estándar', tone: 'ok' },
  { key: 'formal', label: 'Formal', tone: 'warn' },
  { key: 'urgent', label: 'Urgente', tone: 'warn' },
  { key: 'escalation', label: 'Escalado', tone: 'danger' },
];

/**
 * Render descriptor for a chart payload, by its backend `variant`, or null when
 * the generic ChartArtifact should handle it. Each entry adapts the (reduced)
 * chart shape to the viz's own props.
 */
export function resolveChartViz(chart) {
  if (!chart || typeof chart !== 'object') return null;
  switch (chart.variant) {
    case 'treasury_fan':
      if (!Array.isArray(chart.scenarios)) return null;
      return { Component: TreasuryFanChart, domain: 'treasury', props: { scenarios: chart.scenarios, runway_dias: chart.runway_dias, unit: chart.unit } };
    case 'income_expense': {
      const data = Array.isArray(chart.series) ? chart.series : chart.data;
      if (!Array.isArray(data)) return null;
      return { Component: IncomeExpenseArea, domain: 'accounting', props: { data } };
    }
    case 'scenario_compare':
      if (!Array.isArray(chart.rows)) return null;
      return { Component: ScenarioCompare, domain: 'general', props: { title: chart.title, rows: chart.rows, semaforo: chart.semaforo } };
    case 'expense_categories': {
      const categorias = Array.isArray(chart.categorias) ? chart.categorias : chart.data;
      if (!Array.isArray(categorias)) return null;
      return { Component: CategoryTreemap, domain: 'accounting', props: { categorias } };
    }
    case 'break_even':
      if (chart.costesFijos == null) return null;
      return { Component: BreakEvenChart, domain: 'accounting', props: { ...chart } };
    case 'waterfall':
    case 'composition': {
      const items = Array.isArray(chart.items) ? chart.items : (Array.isArray(chart.bars) ? chart.bars : chart.data);
      if (!Array.isArray(items)) return null;
      return { Component: WaterfallChart, domain: chart.domain || 'general', props: { items, title: chart.title } };
    }
    case 'gauge':
    case 'ratio':
      if (chart.fraction == null && chart.value == null) return null;
      return {
        Component: GaugeChart,
        domain: chart.domain || 'accounting',
        props: { fraction: chart.fraction, value: chart.value, max: chart.max, bigLabel: chart.bigLabel, subLabel: chart.subLabel, note: chart.note },
      };
    default:
      return null;
  }
}

/**
 * Render descriptor for an action result, or null when the default
 * ACTION_REGISTRY ResultCard should be used unchanged.
 */
export function resolveResultViz(result) {
  if (!result || typeof result !== 'object') return null;

  if (PATTERN_RESULT_TYPES.has(result.type) && Array.isArray(result.patterns)) {
    return {
      mode: 'replace',
      Component: AnomalyMatrix,
      props: { patterns: result.patterns, periodo: result.periodo, narrativa: result.narrativa },
    };
  }

  if (result.type === 'BULK_REMINDERS' && result.tier_breakdown && typeof result.tier_breakdown === 'object') {
    const segments = TIER_SEGMENTS
      .map((t) => ({ label: t.label, value: Number(result.tier_breakdown[t.key]) || 0, tone: t.tone }))
      .filter((s) => s.value > 0);
    if (segments.length > 0) {
      return {
        mode: 'augment',
        Component: AgingBars,
        props: { title: 'Cartera vencida por urgencia', segments, unit: '' },
      };
    }
  }

  return null;
}

/** Heuristic domain for a reduced chart payload, from its title/unit. */
export function chartDomain(chart) {
  const t = `${chart?.title || ''}`.toLowerCase();
  if (/tesorer|caja|saldo|cash|runway|liquidez/.test(t)) return 'treasury';
  if (/factur|ingreso|ventas|cobro/.test(t)) return 'invoicing';
  if (/nómin|nomina|salario|emplead/.test(t)) return 'payroll';
  if (/iva|impuesto|fiscal|aeat/.test(t)) return 'fiscal';
  if (/gasto|coste|categor/.test(t)) return 'accounting';
  return 'general';
}
